import { FormikContextType } from "formik"
import { ListControllerProps, useUnselectAll, Identifier, useGetMainList, useListController } from "ra-core"
import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { SimpleOption } from "~/components/governance/search/AmbientFacilitiesContext"
import { RecPatch } from "~/dataProvider/tools"
import { MutationCallbacks } from "~/dataProvider/types"
import { TableSchemaResponseDto, FieldMappingInput, EditEncryptionMappingInput, FieldMappingResponseDto, SecondaryClassificationResponseDto } from "~/gen/api/governance"
import { pages, sorts } from "~/utils/paramsWithGetMainList"


export type SendMutateReq = (prevMappings: FieldMappingResponseDto[], mutationInput: EditEncryptionMappingInput, options?: MutationCallbacks) => void | Promise<any>;

export type UseEncryptionMappingMutationHookValue = [
  SendMutateReq,
  {
    error?: any;
    loading: boolean;
    loaded: boolean;
  },
  'edit' | 'update',
]

export interface MutateEncryptionMappingsControllerProps {
  encryptMappingCtrl: MutateEncryptionMappingsContextProps,
}

export interface MutateEncryptionMappingsContextProps {
  currTable?: TableSchemaResponseDto;
  setCurrTable: (x: TableSchemaResponseDto | undefined) => void;
  mappingAmend: MappingAmend;
  setMappingAmend: (x: MappingAmend) => void;
  fieldListCtrl: ListControllerProps<FieldMappingResponseDto & RecPatch>;
  updateEditsCount: (x:number) => void,
  setFormikCtrl: (x:any) => void,
  classifications: SimpleOption[],
}

const initialAmend: MappingAmend = {
  mapped: 0,
  skip: 0,
  'pending-review': 0,
}


export const useMutateEncryptionMappingsController = (mode?: 'edit'|'update'): MutateEncryptionMappingsControllerProps => {
  const [states, setStates] = useState({
    currTable: undefined as TableSchemaResponseDto|undefined,
    mappingAmend: {...initialAmend} as MappingAmend,
  })
  
  const fieldListCtrl = useListController<FieldMappingResponseDto & RecPatch>({ 
    resource: states.currTable ? 'fields' : 'noop', 
    basePath: '', 
    perPage: 400,
    filter: { dbId: states.currTable?.databaseId, tableId: states.currTable?.tableId } 
  })
  const { setFilters, filterValues, } = fieldListCtrl
  useEffect(() => {
    if (mode === 'update') {
      setFilters({ ...filterValues, notNil: (x: FieldMappingResponseDto) => {
        return !x.isSkipped && !x.encryptionRuleId && !x.customEncryptionRuleId
      }}, {})
    }
  }, [])
  
  const { data: classificationData } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})
  const classifications: SimpleOption[] = 
    useMemo(() => 
      Object.values<SecondaryClassificationResponseDto>(classificationData || {})
        .map(c => ({
          ...c, 
          key: c.secondaryClassificationId || -1, 
          text: `${c.primaryClassificationText} - ${c.name}` || ''
        })).sort((a, b) => 
                ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
              || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))), 
      [classificationData]
    )

  const {current: refLookup} = useRef<{ 
    formikCtrl: FormikContextType<Record<Identifier, FieldMappingInput>>,
    mappingEditsCount: number,
  }>({} as any)
    
  const unselectAllFieldSkips = useUnselectAll('fields')

  const mappingAmendRef = useRef<MappingAmend>({ ...initialAmend })
  
  const setCurrTableWithGuard = useCallback((x: TableSchemaResponseDto | undefined) => {
    const { resetForm } = refLookup.formikCtrl
    const thereIsEdit = refLookup.mappingEditsCount > 0

    if (thereIsEdit) {
      const discardContinue = window.confirm('You have unsaved mapping updates, do you wish to discard the changes and switch table?')
      if (!discardContinue) return
    }

    resetForm()
    mappingAmendRef.current = {...initialAmend}
    unselectAllFieldSkips()
    setStates(v => ({...v, currTable: x}))
  }, [unselectAllFieldSkips])

  const setMappingAmend = useCallback((x: MappingAmend) => {
    setStates(v => ({...v, mappingAmend: x}))
  }, [])

  const encryptMappingCtrl = useMemo(() => ({
    currTable: states.currTable,
    setCurrTable: setCurrTableWithGuard,
    mappingAmend: states.mappingAmend,
    setMappingAmend,
    fieldListCtrl,
    updateEditsCount: (x:number) => { refLookup.mappingEditsCount = x },
    setFormikCtrl: (x:any) => { refLookup.formikCtrl = x },
    classifications,
  }), [
    states.currTable,
    states.mappingAmend,
    setCurrTableWithGuard,
    setMappingAmend,
    fieldListCtrl,
    classifications,
    refLookup
  ])

  return {
    encryptMappingCtrl,
  }
}

export type FieldMappingCatagory = 'mapped' | 'skip' | 'pending-review'

type MappingAmend = {
  [k in FieldMappingCatagory]: number;
}

export interface MappingEditEntry {
  direction: [FieldMappingCatagory, FieldMappingCatagory],
  ruleInput: FieldMappingInput
}

